import { useState, type CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { abrir, fila } from '../components/movimiento';
import { api } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useSesion } from '../lib/sesion';
import type { Vista } from '../components/Layout';
import {
  Buscador,
  Cargando,
  Encabezado,
  filtrar,
  Icono,
  Kpi,
  Paginador,
  usePaginado,
  PanelError,
  Vacio,
} from '../components/ui';
import { aviso, botonSecundario, c, chip, encabezadoTabla, enlace, filaTabla, mono, nota, tarjeta } from '../theme';

// La tabla en PC es la de siempre (los 16 px entre columnas son 8 + 8 de cada
// celda); en el celular `.wms-tarjetas` la vuelve una tarjeta por pallet.
const cabeza: CSSProperties = { ...encabezadoTabla, display: undefined, gap: undefined, alignItems: undefined, padding: undefined, paddingBlock: 10, textAlign: 'left', whiteSpace: 'nowrap' };
const celda: CSSProperties = { ...filaTabla, display: undefined, gap: undefined, alignItems: undefined, padding: undefined, paddingBlock: 12, verticalAlign: 'middle' };
const lado = 'px-2 first:pl-4 last:pr-4';

/**
 * Cuánto hace que está cada pallet ubicado: la foto de hoy. No es una
 * liquidación ni un criterio de cobro —el almacenaje se cobra en Resumen ›
 * Quincena, por el día con más pallets—; sirve para contestarle al cliente
 * por un pallet y para ver qué viene quedando.
 */
export default function Estadias({ onIr }: { onIr: (v: Vista, id?: string) => void }) {
  const { vistas } = useSesion();
  const { datos, cargando, error, recargar } = useApi(() => api.estadias(), [], VIVO.normal);
  const [resultado, setResultado] = useState<{ texto: string; ok: boolean } | null>(null);
  const [exportando, setExportando] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  // Buscar y paginar es para encontrar un pallet cuando el cliente pregunta
  // por uno.
  const encontrados = filtrar(datos?.items ?? [], busqueda, (e) => [
    e.pallet,
    e.articulo,
    e.ubicacion,
    e.ingreso,
    e.periodo,
  ]);
  const pagina = usePaginado(encontrados);

  async function exportar() {
    setExportando(true);
    try {
      const nombre = await api.descargarPermanencia();
      setResultado({ texto: `Se descargó ${nombre}.`, ok: true });
    } catch (err) {
      setResultado({ texto: mensajeDeError(err), ok: false });
    } finally {
      setExportando(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Cuántos días lleva hoy cada pallet ubicado en el depósito."
      >
        <button
          type="button"
          onClick={exportar}
          disabled={exportando || !datos}
          style={{ ...botonSecundario, opacity: exportando || !datos ? 0.7 : 1 }}
        >
          <Icono nombre="download" size={19} />
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={exportando ? 'generando' : 'descargar'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.14 }}
            >
              {exportando ? 'Generando…' : 'Descargar permanencia'}
            </motion.span>
          </AnimatePresence>
        </button>
      </Encabezado>

      <div style={nota}>
        Esto no es lo que se cobra: el almacenaje y los movimientos de cada quincena están en Resumen › Quincena.
      </div>

      <AnimatePresence>
        {resultado && (
          <motion.div key={resultado.texto} {...abrir(10)} role={resultado.ok ? 'status' : 'alert'} style={aviso(resultado.ok ? 'ok' : 'err')}>
            {resultado.texto}
          </motion.div>
        )}
      </AnimatePresence>

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : cargando && !datos ? (
        <Cargando texto="Calculando permanencias…" />
      ) : datos && datos.items.length === 0 ? (
        <div style={tarjeta}>
          <Vacio icono="schedule" titulo="No hay pallets ubicados" detalle="Cuando se ubique un pallet en un rack, aparece acá con sus días." />
        </div>
      ) : (
        datos && (
          <>
            <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
              <motion.div {...fila(0)}>
                <Kpi rotulo="1ª quincena" valor={datos.primeraQuincena} pie="día 1 al 15" />
              </motion.div>
              <motion.div {...fila(1)}>
                <Kpi rotulo="2ª quincena o más" valor={datos.segundaOMas} pie="día 16 en adelante" color={c.ambar} />
              </motion.div>
            </div>

            <div className="flex">
              <Buscador
                valor={busqueda}
                onCambiar={setBusqueda}
                placeholder="Pallet, artículo, posición o período"
              />
            </div>

            <div style={{ ...tarjeta, overflow: 'hidden' }}>
              <div className="wms-scroll-x">
                <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th className={lado} style={{ ...cabeza, minWidth: 150 }}>Pallet / artículo</th>
                      <th className={lado} style={{ ...cabeza, width: 100 }}>Ubicación</th>
                      <th className={lado} style={{ ...cabeza, width: 110 }}>Ingreso</th>
                      <th className={lado} style={{ ...cabeza, width: 80, textAlign: 'right' }}>Días</th>
                      <th className={lado} style={{ ...cabeza, width: 160 }}>Quincena de permanencia</th>
                    </tr>
                  </thead>
                  {/* Al buscar las que quedan se deslizan a su lugar; al cambiar de página entran otras. */}
                  <tbody>
                    {pagina.visibles.map((e, i) => (
                      <motion.tr key={e.pallet} layout="position" {...fila(i)}>
                        <td className={`${lado} wms-principal`} style={{ ...celda, minWidth: 150 }}>
                          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, alignItems: 'flex-start' }}>
                            {vistas.includes('frac') ? (
                              <button type="button" onClick={() => onIr('frac', e.pallet)} title="Ver la ficha del pallet" style={{ ...enlace, fontSize: 13, fontWeight: 500 }}>
                                {e.pallet}
                              </button>
                            ) : (
                              <span style={{ fontWeight: 500, fontFamily: mono, fontSize: 13 }}>{e.pallet}</span>
                            )}
                            <span style={{ fontSize: 12, color: c.texto3 }}>{e.articulo}</span>
                          </span>
                        </td>
                        <td className={lado} data-rotulo="Ubicación" style={{ ...celda, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                          {e.ubicacion && vistas.includes('racks') ? (
                            <button type="button" onClick={() => onIr('racks', e.ubicacion!)} title="Ver la posición en el mapa" style={{ ...enlace, fontSize: 12.5, color: c.texto2 }}>
                              {e.ubicacion}
                            </button>
                          ) : (
                            (e.ubicacion ?? '—')
                          )}
                        </td>
                        <td className={lado} data-rotulo="Ingreso" style={{ ...celda, color: c.texto2 }}>{e.ingreso}</td>
                        <td className={lado} data-rotulo="Días" style={{ ...celda, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {e.dias}
                        </td>
                        <td className={lado} data-rotulo="Quincena de permanencia" style={celda}>
                          <span style={chip(e.posiciones >= 2 ? 'warn' : 'ok')}>{e.periodo}</span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {encontrados.length === 0 && (
                <Vacio icono="search" titulo="Sin resultados" detalle={`Ningún pallet coincide con «${busqueda}».`} />
              )}
              <Paginador
                pagina={pagina.pagina}
                paginas={pagina.paginas}
                total={pagina.total}
                desde={pagina.desde}
                que="pallets"
                onIr={pagina.setPagina}
              />
            </div>
          </>
        )
      )}
    </div>
  );
}
